import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { ProjectContext } from "./ProjectContext";
import { useMultiplayer } from "../Multiplayer/useMultiplayer";
import { useTeam } from "../Team";
import { matchPath, useNavigate } from "react-router-dom";
import { ROUTES } from "../../constant/common.constant";
import { useSelector } from "../../common/hooks/useSelector";
import { isEmpty } from "lodash";
import { Image, ProjectWithDesigns } from "../../types";
import ProjectService from "../../service/ProjectService";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

export const ProjectProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const path = window.location.pathname;
  const match = matchPath(`${ROUTES.PROJECT}/*` as any, path);
  const projectId = match?.params?.projectId;
  const [isProjectLoading, setIsProjectLoading] = useState(false);
  const [project, setProject] = useState<ProjectWithDesigns | undefined>(
    undefined
  );
  const { teamId, team } = useTeam();
  const userInfos = useSelector((state) => state.userInfo);

  const { subscribeToProject, unsubscribeFromProject, isConnected } =
    useMultiplayer();

  const getProject = useCallback(
    async (options?: { skipLoading?: boolean }) => {
      if (!options?.skipLoading) {
        setIsProjectLoading(true);
      }
      if (!projectId) {
        return;
      }
      try {
        const { data } = await ProjectService.getProjectWithDesigns(projectId);
        setProject(data.data);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          setProject(undefined);
          toast.warn(t("project.not_found"));
          navigate(ROUTES.HOME, { replace: true });
        }
      } finally {
        setIsProjectLoading(false);
      }
    },
    [t, navigate, projectId]
  );

  const clearProject = useCallback(() => {
    setProject(undefined);
  }, []);

  useEffect(() => {
    if (!projectId || !isConnected || team?.teammates == null) return;
    subscribeToProject(projectId);

    return () => {
      unsubscribeFromProject();
    };
  }, [
    projectId,
    isConnected,
    team?.teammates,
    subscribeToProject,
    unsubscribeFromProject,
    userInfos,
  ]);

  const updateProjectDesignImages = useCallback(
    (designId: string, images: Image[]) => {
      setProject((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          designs: prev.designs.map((d) =>
            d.id === designId ? { ...d, images } : d
          ),
        };
      });
    },
    []
  );

  useEffect(() => {
    if (!userInfos || isEmpty(userInfos) || !userInfos.id) {
      return;
    }
  }, [teamId, userInfos, getProject, projectId]);

  return (
    <ProjectContext.Provider
      value={{
        project,
        projectId,
        isProjectLoading,
        refetchProject: getProject,
        clearProject,
        updateProjectDesignImages,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
